import React, { useState, useEffect } from 'react'
import miningService from '../services/miningService'
import { useLanguage } from '../i18n'
import { useMockData, getMockFuelPoolData } from '../services/mockData'
import API_CONFIG from '../config/api'

// 燃料池统计组件
const FuelPoolStats = ({ fuelStats, t }) => {
  if (!fuelStats) {
    return (
      <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
        <div className="flex items-center justify-center py-8">
          <div className="loading-spinner"></div>
          <span className="ml-3 text-gray-300">{t('fuelDashboard.loadingFuelData')}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
      <h3 className="text-2xl font-bold mb-6 text-center">{t('fuelDashboard.fuelPoolStats')}</h3>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="text-center p-4 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-2xl">
          <div className="text-3xl font-bold text-blue-400">{fuelStats.totalParticipants}</div>
          <div className="text-sm text-gray-300">{t('fuelDashboard.participants')}</div>
        </div>
        
        <div className="text-center p-4 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-2xl">
          <div className="text-3xl font-bold text-green-400">{fuelStats.totalInteractions}</div>
          <div className="text-sm text-gray-300">{t('fuelDashboard.totalInteractions')}</div>
        </div>
        
        <div className="text-center p-4 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-2xl">
          <div className="text-3xl font-bold text-purple-400">{fuelStats.totalGasConsumed.toFixed(1)}</div>
          <div className="text-sm text-gray-300">{t('fuelDashboard.totalGas')}</div>
        </div>
        
        <div className="text-center p-4 bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-2xl">
          <div className="text-3xl font-bold text-orange-400">{fuelStats.topDailyPoints}</div>
          <div className="text-sm text-gray-300">{t('fuelDashboard.topPoints')}</div>
        </div>
      </div>
    </div>
  )
}

// 用户燃料池数据组件
const UserFuelData = ({ userRecord, fuelStats, t }) => {
  if (!userRecord) {
    return (
      <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
        <h4 className="text-xl font-semibold mb-4">{t('fuelDashboard.myFuelData')}</h4>
        <p className="text-gray-400 text-center py-4">{t('fuelDashboard.noFuelData')}</p>
      </div>
    )
  }

  const rank = fuelStats?.topRankings?.findIndex(r => r.wallet_address === userRecord.wallet_address) + 1
  const isTop100 = rank && rank <= 100

  return (
    <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
      <h4 className="text-xl font-semibold mb-4">{t('fuelDashboard.myFuelData')}</h4>
      
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-gray-300">{t('fuelDashboard.interactionCount')}:</span>
          <span className="font-semibold text-green-400">{userRecord.interaction_count}</span>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-gray-300">{t('fuelDashboard.gasConsumed')}:</span>
          <span className="font-semibold text-purple-400">{userRecord.gas_consumed.toFixed(2)}</span>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-gray-300">{t('fuelDashboard.dailyPoints')}:</span>
          <span className="font-semibold text-blue-400">{userRecord.daily_points}</span>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-gray-300">{t('fuelDashboard.currentRank')}:</span>
          <span className={`font-semibold ${isTop100 ? 'text-orange-400' : 'text-gray-400'}`}>
            {rank ? `#${rank}` : t('fuelDashboard.notOnList')}
          </span>
        </div>
        
        {isTop100 && (
          <div className="mt-4 p-3 bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-lg border border-orange-500/30">
            <p className="text-sm text-orange-300 text-center">
              {t('fuelDashboard.top100Message')}
            </p>
          </div>
        )}
        
        {userRecord.has_referrer && (
          <div className="mt-2 p-2 bg-gradient-to-r from-green-500/10 to-blue-500/10 rounded-lg">
            <p className="text-xs text-green-400 text-center">
              {t('fuelDashboard.referralBonusActive')}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

// 幸运探索者公告栏组件
const LuckyExplorersBoard = ({ prizeWinners, t }) => {
  if (!prizeWinners || prizeWinners.length === 0) {
    return (
      <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
        <h4 className="text-xl font-semibold mb-4">{t('fuelDashboard.luckyExplorers')}</h4>
        <p className="text-gray-400 text-center py-4">{t('fuelDashboard.noWinners')}</p>
      </div>
    )
  }

  // 按日期分组
  const winnersByDate = prizeWinners.reduce((acc, winner) => {
    const date = winner.prize_date
    if (!acc[date]) acc[date] = []
    acc[date].push(winner)
    return acc
  }, {})

  return (
    <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
      <h4 className="text-xl font-semibold mb-4">{t('fuelDashboard.luckyExplorers')}</h4>
      
      <div className="space-y-4 max-h-80 overflow-y-auto">
        {Object.entries(winnersByDate).map(([date, winners]) => (
          <div key={date} className="border-b border-gray-600/30 pb-4 last:border-0">
            <div className="flex items-center mb-2">
              <span className="text-sm font-medium text-gray-300">{date}</span>
              <span className="ml-2 text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded">
                {winners.length} {t('fuelDashboard.winners')}
              </span>
            </div>
            
            <div className="space-y-2">
              {winners.map((winner, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-800/30 rounded-lg">
                  <div className="flex items-center">
                    <span className="w-6 h-6 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full flex items-center justify-center text-xs font-bold text-black mr-2">
                      {winner.rank}
                    </span>
                    <span className="text-sm font-mono">
                      {winner.wallet_address.slice(0, 8)}...{winner.wallet_address.slice(-6)}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-green-400 font-semibold">+{winner.prize_amount} USDT</span>
                    <div className="text-xs text-gray-400">{winner.daily_points}{t('fuelDashboard.points')}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-4 text-center">
        <p className="text-xs text-gray-400">
          {t('fuelDashboard.lotteryInfo')}
        </p>
      </div>
    </div>
  )
}

// 业绩排行榜组件
const Leaderboard = ({ topRankings, t }) => {
  if (!topRankings || topRankings.length === 0) {
    return (
      <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
        <h4 className="text-xl font-semibold mb-4">{t('fuelDashboard.todayLeaderboard')}</h4>
        <p className="text-gray-400 text-center py-4">{t('fuelDashboard.noRankingData')}</p>
      </div>
    )
  }

  return (
    <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
      <h4 className="text-xl font-semibold mb-4">{t('fuelDashboard.todayLeaderboardTop20')}</h4>
      
      <div className="space-y-2 max-h-80 overflow-y-auto">
        {topRankings.map((user, index) => (
          <div key={index} className={`flex items-center justify-between p-3 rounded-lg ${
            index < 3 
              ? 'bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20' 
              : 'bg-gray-800/30'
          }`}>
            <div className="flex items-center">
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3 ${
                index === 0 ? 'bg-yellow-500 text-black' :
                index === 1 ? 'bg-gray-400 text-black' :
                index === 2 ? 'bg-orange-500 text-black' :
                'bg-gray-600 text-gray-300'
              }`}>
                {index + 1}
              </span>
              <span className="font-mono text-sm">
                {user.wallet_address.slice(0, 6)}...{user.wallet_address.slice(-4)}
              </span>
            </div>
            
            <div className="text-right">
              <span className="font-semibold">{user.daily_points}</span>
              <div className="text-xs text-gray-400">
                {user.interaction_count}{t('fuelDashboard.interactions')}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// 主燃料池仪表盘组件
const FuelPoolDashboard = ({ isConnected, walletAddress }) => {
  const { t } = useLanguage()
  const [fuelData, setFuelData] = useState(null)
  const [userFuelData, setUserFuelData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadFuelPoolData()
    
    // 设置定时刷新
    const interval = setInterval(() => {
      loadFuelPoolData(false)
    }, 30000) // 30秒刷新一次

    return () => clearInterval(interval)
  }, [isConnected, walletAddress])

  const loadFuelPoolData = async (showLoading = true) => {
    try {
      if (showLoading) setIsLoading(true)
      
      // 检查是否使用模拟数据
      if (!API_CONFIG.hasBackend()) {
        const mockData = getMockFuelPoolData()
        setFuelData(mockData)
        setUserFuelData({ userRecord: mockData.userRecord })
        if (showLoading) setIsLoading(false)
        return
      }
      
      // 获取燃料池数据
      const response = await fetch(`${API_CONFIG.baseURL}/fuel-pool`)
      const data = await response.json()
      
      if (data.success) {
        setFuelData(data.data)
      }

      // 如果已连接钱包，获取用户数据
      if (isConnected && walletAddress) {
        try {
          const userResponse = await fetch(`${API_CONFIG.baseURL}/fuel-pool/user/${walletAddress}`)
          const userData = await userResponse.json()
          
          if (userData.success) {
            setUserFuelData(userData.data)
          }
        } catch (error) {
          console.error('获取用户燃料池数据失败:', error)
        }
      }

    } catch (error) {
      console.error('加载燃料池数据失败:', error)
      // 出错时使用模拟数据
      const mockData = getMockFuelPoolData()
      setFuelData(mockData)
      setUserFuelData({ userRecord: mockData.userRecord })
    } finally {
      if (showLoading) setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
        <span className="ml-3 text-gray-300">{t('fuelDashboard.loadingFuelData')}</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 燃料池统计 */}
      <FuelPoolStats fuelStats={fuelData?.fuelStats} t={t} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 用户数据 */}
        <UserFuelData 
          userRecord={userFuelData?.userRecord} 
          fuelStats={fuelData}
          t={t}
        />

        {/* 业绩排行榜 */}
        <Leaderboard topRankings={fuelData?.topRankings} t={t} />
      </div>

      {/* 幸运探索者公告栏 */}
      <LuckyExplorersBoard prizeWinners={fuelData?.prizeWinners} t={t} />

      {/* 规则说明 */}
      <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 border-2 border-gray-700/50 rounded-3xl p-6 shadow-2xl">
        <h4 className="text-xl font-semibold mb-4">{t('fuelDashboard.fuelPoolRules')}</h4>
        <div className="space-y-2 text-sm text-gray-300">
          <p>• {t('fuelDashboard.rule1')}</p>
          <p>• {t('fuelDashboard.rule2')}</p>
          <p>• {t('fuelDashboard.rule3')}</p>
          <p>• {t('fuelDashboard.rule4')}</p>
          <p>• {t('fuelDashboard.rule5')}</p>
        </div>
      </div>
    </div>
  )
}

export default FuelPoolDashboard
